/** 项目讨论负责留言、附件、提醒与转待办；作者可不限时撤回重编或删除，保留回复和待办。 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { uploadChunked } from "../lib/upload";
import { authStore } from "../lib/api";
import { createPortal } from "react-dom";
import { toast } from "./ui/sonner";
import { Archive, ChevronDown, ListTodo, Loader2, Lock, Maximize2, MessageCircle, Minimize2, Paperclip, Pencil, Pin, Send, ThumbsUp, Trash2, Undo2, X, MessagesSquare } from "lucide-react";
import { HubDiscussionTodoDialog } from "./HubDiscussionTodoDialog";
import { HubDiscussionMessageDialog } from "./HubDiscussionMessageDialog";
import { ProjectKeywordInput, useProjectKeywordSearch } from "./ProjectKeywordSearch";

import { T } from "../lib/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const furl = (f) => `${BACKEND}/api/files/${f.fileId}/download?auth=${authStore.get("ali_token") || ""}`;

const FileChips = ({ files }) => (files || []).length === 0 ? null : (
  <div className="mt-1.5 flex flex-wrap gap-1.5">
    {files.map((f) => (f.contentType || "").startsWith("image") ? (
      <a key={f.fileId} href={furl(f)} target="_blank" rel="noreferrer" data-testid={`hub-disc-file-${f.fileId}`}
        className="block h-16 w-16 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
        <img src={furl(f)} alt={f.filename} className="h-full w-full object-cover" loading="lazy" />
      </a>
    ) : (
      <a key={f.fileId} href={furl(f)} target="_blank" rel="noreferrer" data-testid={`hub-disc-file-${f.fileId}`}
        className="inline-flex max-w-full items-center gap-1 break-all text-[10px] font-semibold text-indigo-600 bg-indigo-50 rounded-full px-2 py-1 hover:bg-indigo-100">
        <Paperclip className="h-3 w-3 shrink-0" />{f.filename || "file"}
      </a>
    ))}
  </div>
);

const DEPT_EN = {
  "业主": "Owner", "设计部": "Design", "工程部": "Construction Dept", "行政部": "Admin", "销售部": "Sales",
  "财务部": "Finance", "管理层": "Mgmt", "市场部": "Marketing", "人事部": "HR", "施工队": "Crew", "仓储部": "Warehouse", "员工": "Staff",
};
const deptLabel = (dept, zh) => (zh ? dept : (DEPT_EN[dept] || dept));
const DEPT_BADGE = {
  "业主": "bg-blue-100 text-blue-700", "设计部": "bg-emerald-100 text-emerald-700", "工程部": "bg-red-100 text-red-700",
  "行政部": "bg-amber-100 text-amber-700", "销售部": "bg-indigo-100 text-indigo-700", "财务部": "bg-teal-100 text-teal-700",
  "管理层": "bg-slate-800 text-white", "市场部": "bg-pink-100 text-pink-700", "人事部": "bg-cyan-100 text-cyan-700",
  "施工队": "bg-orange-100 text-orange-700", "仓储部": "bg-lime-100 text-lime-700",
};
const AVATAR_BG = ["bg-violet-500", "bg-emerald-500", "bg-pink-500", "bg-slate-500", "bg-blue-500", "bg-amber-500"];
const avatarColor = (name) => AVATAR_BG[(name || "?").charCodeAt(0) % AVATAR_BG.length];

const fmtTime = (iso, zh = true) => {
  try {
    const d = new Date(iso); const now = new Date();
    const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    if (d.toDateString() === now.toDateString()) return `${zh ? "今天" : "Today"} ${hm}`;
    if (new Date(now.getTime() - 86400000).toDateString() === d.toDateString()) return `${zh ? "昨天" : "Yesterday"} ${hm}`;
    return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${hm}`;
  } catch { return ""; }
};
const renderText = (text) => String(text || "").split(/(@[^\s@，。,]+)/g).map((seg, i) =>
  seg.startsWith("@") ? <span key={i} className="text-indigo-600 font-semibold">{seg}</span> : seg);

const VisBadge = ({ owner, zh }) => owner ? (
  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 inline-flex items-center gap-1">
    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />{zh ? "业主可见" : "Owner"}</span>
) : (
  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 inline-flex items-center gap-1">
    <Lock className="h-2.5 w-2.5" />{zh ? "仅内部" : "Internal"}</span>
);

/** 按作者账号显示左右消息气泡；保留现有回复、置顶、归档和附件操作，增加待办入口。 */
function DiscItem({ d, zh, user, like, onReply, onPin, onArchive, onTodo, onMessageAction, avatarId, searchTerm }) {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState("");
  // 命中回复时自动展开，避免搜索结果只显示父留言而看不到命中的内容。
  useEffect(() => {
    const q = (searchTerm || "").trim().toLocaleLowerCase();
    if (q && (d.replies || []).some((reply) => [reply.authorName, reply.authorUsername,
      ...(reply.recalledAt ? [] : [reply.text, ...(reply.files || []).map((file) => file.filename)])]
      .some((value) => String(value || "").toLocaleLowerCase().includes(q)))) setOpen(true);
  }, [d.replies, searchTerm]);
  // 与沟通聊天统一：以账号而非显示名判断作者，自己的头像和消息靠右，正文仍左对齐便于阅读。
  const mine = !!user?.username && d.authorUsername === user.username;
  const hidden = !!(d.recalledAt || d.deletedAt);
  // 主留言和回复采用相同作者规则；不因管理员角色而出现代删/代编辑他人消息的入口。
  const authorActions = (entry, reply = null) => entry.authorUsername === user?.username && !entry.deletedAt && <>
    <button type="button" data-testid={`hub-disc-${entry.recalledAt ? "reedit" : "recall"}-${entry.id}`} onClick={() => onMessageAction({ discussion: d, reply, action: entry.recalledAt ? "edit" : "recall" })} className="inline-flex min-h-6 items-center gap-1 rounded hover:text-brand-600 focus-visible:ring-2 focus-visible:ring-brand-400">
      {entry.recalledAt ? <Pencil className="h-3 w-3" /> : <Undo2 className="h-3 w-3" />}{entry.recalledAt ? (zh ? "重新编辑" : "Edit again") : (zh ? "撤回" : "Recall")}
    </button>
    <button type="button" data-testid={`hub-disc-delete-${entry.id}`} onClick={() => onMessageAction({ discussion: d, reply, action: "delete" })} className="inline-flex min-h-6 items-center gap-1 rounded hover:text-destructive focus-visible:ring-2 focus-visible:ring-brand-400"><Trash2 className="h-3 w-3" />{zh ? "删除" : "Delete"}</button>
  </>;
  const messageText = (entry) => entry.deletedAt ? (zh ? "该消息已删除" : "This message was deleted") : entry.recalledAt ? (zh ? "该消息已撤回" : "This message was recalled") : renderText(entry.text);
  const liked = (d.likes || []).includes(user?.username);
  const canMod = !!user && (["admin", "ceo", "eng_manager", "pm"].includes(user.role) || d.authorUsername === user.username);
  const participants = [...new Set((d.replies || []).map((r) => r.authorName).filter(Boolean))];
  const submit = () => { if (txt.trim()) { onReply(d, txt); setTxt(""); } };
  return (
    <div className={`w-fit max-w-[95%] sm:max-w-[90%] rounded-xl border p-3 transition-colors ${mine ? "ml-auto bg-brand-50 border-brand-100 hover:border-brand-200" : "mr-auto bg-white border-slate-100 hover:border-slate-200"}`}
      id={`hub-disc-item-${d.id}`} data-testid={`hub-disc-item-${d.id}`} data-mine={mine ? "1" : "0"}>
      <div className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}>
        {avatarId
          ? <img src={`${BACKEND}/api/files/${avatarId}/download?auth=${authStore.get("ali_token") || ""}`} alt={d.authorName}
              className="h-8 w-8 rounded-full object-cover border border-slate-200 shrink-0" data-testid={`hub-disc-avatar-img-${d.id}`} />
          : <div className={`h-8 w-8 rounded-full ${avatarColor(d.authorName)} text-white text-xs font-bold flex items-center justify-center shrink-0`}>{(d.authorName || "?")[0].toUpperCase()}</div>}
        <div className="min-w-0 flex-1">
          <p className={`flex items-center gap-1.5 flex-wrap text-xs ${mine ? "justify-end" : ""}`}>
            <span className="font-bold text-slate-800">{d.authorName}</span>
            {d.dept && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${DEPT_BADGE[d.dept] || "bg-slate-100 text-slate-600"}`}>{deptLabel(d.dept, zh)}</span>}
            <VisBadge owner={d.visibility === "owner"} zh={zh} />
            {d.source === "chat" && <button type="button" data-testid={`hub-disc-from-chat-${d.id}`} title={zh ? "来自沟通「项目群」，点击在聊天中查看" : "From the project chat — open"} onClick={() => window.dispatchEvent(new CustomEvent("chat:open", { detail: { channelId: d.chatChannelId, messageId: d.chatMessageId } }))} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-50 text-[#4F6BFF] border border-indigo-100 inline-flex items-center gap-0.5 hover:bg-indigo-100"><MessagesSquare className="h-2.5 w-2.5" />{zh ? "项目群" : "Chat"}</button>}
            {d.pinned && <span data-testid={`hub-disc-pinned-badge-${d.id}`} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-0.5"><Pin className="h-2.5 w-2.5" />{zh ? "置顶" : "Pinned"}</span>}
            <span className="text-slate-300 text-[10px]">{fmtTime(d.createdAt, zh)}</span>
          </p>
          <p className={`text-xs mt-1 leading-relaxed whitespace-pre-wrap break-words ${hidden ? "text-slate-400" : "text-slate-700"}`} data-testid={`hub-disc-text-${d.id}`}>{messageText(d)}{!hidden && d.editedAt && <span className="ml-1.5 text-[10px] text-slate-400">{zh ? "已编辑" : "Edited"}</span>}</p>
          <FileChips files={d.files} />
          {d.relatedLabel && (
            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-red-400 inline-block" />{zh ? "关联" : "Related"}: {d.relatedLabel}</p>
          )}
          <p className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-1.5 text-[10px] text-slate-400 ${mine ? "justify-end" : ""}`}>
            <button data-testid={`hub-disc-reply-btn-${d.id}`} onClick={() => setOpen(!open)} className="flex items-center gap-1 hover:text-indigo-600">
              <MessageCircle className="h-3 w-3" />{(d.replies || []).length} {zh ? "回复" : "replies"}</button>
            {!hidden && <button data-testid={`hub-disc-like-${d.id}`} onClick={() => like(d)} className={`flex items-center gap-1 hover:text-amber-600 ${liked ? "text-amber-600 font-bold" : ""}`}>
              <ThumbsUp className="h-3 w-3" />{(d.likes || []).length}</button>}
            {d.todoId ? <a href={`/todos?id=${encodeURIComponent(d.todoId)}`} data-testid={`hub-disc-todo-link-${d.id}`} className="flex items-center gap-1 text-emerald-600 hover:underline">
              <ListTodo className="h-3 w-3" />{zh ? "已转待办" : "Open to-do"}</a> : !hidden && <button type="button" data-testid={`hub-disc-to-todo-${d.id}`} onClick={() => onTodo(d)} className="flex items-center gap-1 hover:text-emerald-600">
              <ListTodo className="h-3 w-3" />{zh ? "转待办" : "Create to-do"}</button>}
            {authorActions(d)}
            {canMod && !hidden && (
              <>
                <button data-testid={`hub-disc-pin-${d.id}`} onClick={() => onPin(d)} className={`flex items-center gap-0.5 hover:text-amber-600 ${d.pinned ? "text-amber-600 font-bold" : ""}`}>
                  <Pin className="h-3 w-3" />{d.pinned ? (zh ? "取消置顶" : "Unpin") : (zh ? "置顶" : "Pin")}</button>
                <button data-testid={`hub-disc-archive-${d.id}`} onClick={() => onArchive(d)} className={`flex items-center gap-0.5 hover:text-emerald-600 ${d.archived ? "text-emerald-600 font-bold" : ""}`}>
                  <Archive className="h-3 w-3" />{d.archived ? (zh ? "取消归档" : "Unarchive") : (zh ? "已解决·归档" : "Archive")}</button>
              </>
            )}
            {participants.length > 0 && <span className="truncate">{zh ? "参与" : "By"}: {participants.join("、")}</span>}
          </p>
          {open && (
            <div className="mt-2 space-y-1.5 border-l-2 border-slate-100 pl-2.5">
              {(d.replies || []).map((r) => (
                <div key={r.id} className="text-[11px] text-slate-600">
                  <span className="font-bold text-slate-700">{r.authorName}</span>
                  {r.dept && <span className={`ml-1 text-[8px] font-bold px-1 py-0.5 rounded ${DEPT_BADGE[r.dept] || "bg-slate-100 text-slate-500"}`}>{deptLabel(r.dept, zh)}</span>}
                  {r.source === "chat" && <span data-testid={`hub-disc-reply-from-chat-${r.id}`} className="ml-1 text-[8px] font-bold px-1 py-0.5 rounded bg-indigo-50 text-[#4F6BFF]">{zh ? "项目群" : "Chat"}</span>}
                  <span className="whitespace-pre-wrap break-words" data-testid={`hub-disc-text-${r.id}`}>{" "}{messageText(r)}</span> <span className="text-slate-300">{fmtTime(r.at, zh)}</span>
                  {!r.recalledAt && r.editedAt && <span className="ml-1 text-slate-400">{zh ? "已编辑" : "Edited"}</span>}
                  <FileChips files={r.files} />
                  <div className="flex flex-wrap gap-x-3 text-[10px] text-slate-400">{authorActions(r, r)}</div>
                </div>
              ))}
              <div className="flex gap-1">
                <input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder={zh ? "回复…" : "Reply…"} className="flex-1 text-[11px] rounded border border-slate-200 px-1.5 py-1"
                  data-testid={`hub-disc-reply-input-${d.id}`} />
                <button onClick={submit} className="text-[11px] text-white bg-indigo-600 rounded px-2" data-testid={`hub-disc-reply-send-${d.id}`}>{zh ? "回复" : "Send"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const HubDiscussion = ({ api, id, zh, user, tasks, phases }) => {
  const [data, setData] = useState({ items: [], staff: [] });
  const [filter, setFilter] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [vis, setVis] = useState("internal");
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [related, setRelated] = useState("");
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [upBusy, setUpBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [full, setFull] = useState(false);
  const [todoDiscussion, setTodoDiscussion] = useState(null);
  const [messageAction, setMessageAction] = useState(null);
  const search = useProjectKeywordSearch({ api, projectId: id, kind: "discussions", keyword, filter });
  const refreshSearch = search.refresh;
  const inputRef = useRef(null);
  const locatedDiscussion = useRef(null);

  // 切换项目时关闭旧消息表单，避免把上个项目的讨论 ID 提交到新项目。
  useEffect(() => { setMessageAction(null); setTodoDiscussion(null); setKeyword(""); }, [id]);
  // 搜索时展开已归档的匹配项，但用户仍可手动收起。
  useEffect(() => { if (search.active) setShowArchived(true); }, [search.active]);

  // 待办来源直接定位原讨论；异步加载完成后再滚动，已归档的原文也自动展开。
  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    if (!anchor.startsWith("hub-disc-item-") || locatedDiscussion.current === anchor) return undefined;
    const item = data.items.find((d) => `hub-disc-item-${d.id}` === anchor);
    if (!item) return undefined;
    if (item.archived) setShowArchived(true);
    const frame = requestAnimationFrame(() => {
      document.getElementById(anchor)?.scrollIntoView({ block: "center", behavior: "instant" });
      locatedDiscussion.current = anchor;
    });
    return () => cancelAnimationFrame(frame);
  }, [data.items]);

  // 原单行输入框会横向隐藏长文。现在按实际换行向下展开，沿用聊天框的 160px 上限，
  // 超出后在框内滚动；删除、发送清空、@人填入和全屏切换都重新测量，避免残留旧高度。
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;
    const resize = () => {
      const scrollTop = input.scrollTop;
      input.style.height = "auto";
      input.style.overflowY = "hidden";
      // 空草稿保持一行，不能让长占位提示在窄屏上把编辑区撑高。
      if (!input.value) return;
      const borderHeight = input.offsetHeight - input.clientHeight;
      input.style.height = `${Math.min(input.scrollHeight + borderHeight, 160)}px`;
      input.style.overflowY = input.scrollHeight > input.clientHeight ? "auto" : "hidden";
      input.scrollTop = scrollTop;
    };
    resize();
    // 页面侧栏或窗口变化也会改变换行；只响应宽度变化，避免自身增高触发循环测量。
    let width = input.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const nextWidth = input.getBoundingClientRect().width;
      if (nextWidth !== width) { width = nextWidth; resize(); }
    });
    observer.observe(input);
    return () => observer.disconnect();
  }, [text, full]);

  useEffect(() => {
    if (!full) return undefined;
    // 转待办弹窗独立处理 Esc；不能在关闭表单时同时退出项目讨论全屏。
    const onKey = (e) => { if (e.key === "Escape" && !e.defaultPrevented && !todoDiscussion && !messageAction) setFull(false); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [full, todoDiscussion, messageAction]);

  const uploadFiles = async (fl) => {
    setUpBusy(true);
    try {
      const added = [];
      for (const file of Array.from(fl).slice(0, 6)) {
        if (file.size > 20 * 1024 * 1024) { toast.error(zh ? `「${file.name}」超过 20MB 上限，已跳过` : `${file.name} exceeds 20MB`); continue; }
        const fin = await uploadChunked(api, file);
        added.push({ fileId: fin.fileId, filename: file.name, contentType: fin.contentType || file.type });
      }
      setFiles((prev) => [...prev, ...added]);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "附件上传失败" : "Attachment upload failed")); }
    setUpBusy(false);
  };

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/projects/${id}/hub/discussions`); setData(d); } catch {}
    refreshSearch();
  }, [api, id, refreshSearch]);
  useEffect(() => { load(); }, [load]);
  // 项目群 ↔ 项目讨论实时同步：悬浮沟通窗转发的聊天事件命中本项目 → 自动刷新（无需手动刷新页面）
  useEffect(() => {
    let t = null;
    const h = (e) => { const d = e.detail?.data || {}; if (d.projectId !== id) return; clearTimeout(t); t = setTimeout(load, 400); };
    window.addEventListener("chat:event", h);
    return () => { clearTimeout(t); window.removeEventListener("chat:event", h); };
  }, [id, load]);

  const displayedItems = search.active ? search.items : data.items;
  const activeItems = displayedItems.filter((x) => !x.archived);
  const archivedItems = displayedItems.filter((x) => x.archived);
  // 顶部分类数量保持原列表口径；搜索结果另按当前分类查询，不把首屏数量误当作历史总数。
  const originalActiveItems = useMemo(() => data.items.filter((x) => !x.archived), [data.items]);
  const counts = useMemo(() => ({
    all: originalActiveItems.length,
    internal: originalActiveItems.filter((x) => x.visibility !== "owner").length,
    owner: originalActiveItems.filter((x) => x.visibility === "owner").length,
  }), [originalActiveItems]);
  const list = activeItems.filter((x) => filter === "all" ? true : filter === "owner" ? x.visibility === "owner" : x.visibility !== "owner");
  const avatarMap = useMemo(() => Object.fromEntries((data.staff || []).filter((s) => s.avatarFileId).map((s) => [s.username, s.avatarFileId])), [data.staff]);

  const mq = text.match(/@([^\s@]*)$/);
  // @ 名单由项目接口按“项目负责人及协办 → 本项目小组 → 其他同事”排序。
  // 过去在这里截取前 6 人会直接藏掉后面的备选同事，也可能在名单无序时挤掉项目负责人；
  // 下拉框已有固定高度和内部滚动，保留全部匹配项才能按需 @ 到跨项目同事。
  const cands = mq ? data.staff.filter((s) => !mq[1] || (s.name || "").toLowerCase().includes(mq[1].toLowerCase())) : [];
  const pick = (s) => {
    setText(text.replace(/@([^\s@]*)$/, `@${s.name} `));
    if (!mentions.includes(s.username)) setMentions([...mentions, s.username]);
  };

  const send = async () => {
    if ((!text.trim() && files.length === 0) || busy || upBusy) return;
    setBusy(true);
    try {
      const t = related ? (tasks || []).find((x) => x.id === related) : null;
      await api.post(`/projects/${id}/hub/discussions`, {
        text, visibility: vis, mentions, files,
        relatedTaskId: related || null, relatedLabel: t ? `${phases[t.phase]}·${t.title}` : null,
      });
      setText(""); setMentions([]); setRelated(""); setFiles([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  const like = async (d) => { try { await api.post(`/projects/${id}/hub/discussions/${d.id}/like`); load(); } catch {} };
  const onPin = async (d) => {
    try { const { data: r } = await api.post(`/projects/${id}/hub/discussions/${d.id}/pin`); toast.success(r.pinned ? (zh ? "已置顶" : "Pinned") : (zh ? "已取消置顶" : "Unpinned")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
  };
  const onArchive = async (d) => {
    try { const { data: r } = await api.post(`/projects/${id}/hub/discussions/${d.id}/archive`); toast.success(r.archived ? (zh ? "已归档（讨论区已折叠收起）" : "Archived") : (zh ? "已取消归档" : "Unarchived")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
  };
  const onReply = async (d, txt) => {
    try { await api.post(`/projects/${id}/hub/discussions/${d.id}/reply`, { text: txt }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
  };

  const pill = (key, label, count) => (
    <button data-testid={`hub-disc-filter-${key}`} onClick={() => setFilter(key)}
      className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${filter === key ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
      {label} {count}
    </button>
  );

  const panel = (
    <div className={full ? "fixed inset-0 z-[60] bg-white flex flex-col p-4 sm:p-6 overflow-hidden" : "bg-white rounded-xl border border-slate-200 p-4"} data-testid="hub-disc-panel" data-full={full ? "1" : "0"}>
      <div className={`flex items-center justify-between mb-3 flex-wrap gap-2 ${full ? "mobile-safe-topbar max-w-4xl w-full mx-auto" : ""}`}>
        <p data-testid="hub-disc-title" onDoubleClick={() => setFull((v) => !v)} title={zh ? "双击：放大 / 还原" : "Double-click: enlarge / restore"}
          className={`font-bold text-slate-800 flex items-center gap-1.5 select-none ${full ? "text-lg cursor-zoom-out" : "text-sm cursor-zoom-in"}`}><MessageCircle className="h-4 w-4 text-violet-600" />{zh ? "项目讨论" : "Discussion"}</p>
        <div className="flex gap-1.5 items-center">
          {pill("all", zh ? "全部" : "All", counts.all)}
          {pill("internal", zh ? "仅内部" : "Internal", counts.internal)}
          {pill("owner", zh ? "业主可见" : "Owner", counts.owner)}
          {/* 二-15：原「项目群」跳转按钮已删除。老板原话（43:16-44:30）：项目主界面显示的本来就是
              本项目的聊天记录，没必要再点「项目群」精准跳到同一个群，属于重复功能。
              讨论条目上的「项目群」来源徽标（回到原话）仍保留，见下方 DiscItem。 */}
          <button data-testid="hub-disc-fullscreen-toggle" onClick={() => setFull((v) => !v)} title={full ? (zh ? "还原（Esc）" : "Restore (Esc)") : (zh ? "放大显示" : "Enlarge")}
            className={`inline-flex items-center gap-1 rounded-lg border text-[11px] font-semibold transition-colors ${full ? "px-2.5 py-1.5 border-slate-900 bg-slate-900 text-white hover:bg-slate-700" : "p-1.5 border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
            {full ? <><Minimize2 className="h-3.5 w-3.5" />{zh ? "还原" : "Restore"}</> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      <div className={`mb-3 ${full ? "max-w-4xl w-full mx-auto" : ""}`}>
        <ProjectKeywordInput value={keyword} onChange={setKeyword} zh={zh} testId="hub-disc-keyword"
          label={zh ? "搜索讨论、回复、发布人或附件" : "Search discussions, replies, authors or attachments"} />
        {search.active && search.loading && <p role="status" className="mt-2 text-xs text-slate-500">{zh ? "搜索中…" : "Searching…"}</p>}
        {search.active && search.error && <p role="alert" className="mt-2 text-xs text-red-600">
          {zh ? "搜索失败，请重试" : "Search failed. Please retry."}{" "}
          <button type="button" onClick={search.refresh} className="underline focus-visible:ring-2 focus-visible:ring-brand-400">{zh ? "重试" : "Retry"}</button>
        </p>}
      </div>
      <div className={`space-y-2.5 overflow-y-auto ${full ? "min-h-0 flex-1 max-w-4xl w-full mx-auto" : "max-h-[480px]"}`}>
        {list.length === 0 && (!search.active || (!search.loading && !search.error)) && <p className="text-xs text-slate-300 text-center py-6" data-testid="hub-disc-empty">
          {search.active ? (archivedItems.length ? (zh ? "没有未归档的匹配讨论" : "No matching active discussions") : (zh ? "没有匹配的讨论" : "No matching discussions")) : (zh ? "还没有讨论 — 在下方发起第一条" : "No discussions yet")}
        </p>}
        {list.map((d) => <DiscItem key={d.id} d={d} zh={zh} user={user} like={like} onReply={onReply} onPin={onPin} onArchive={onArchive} onTodo={setTodoDiscussion} onMessageAction={setMessageAction} avatarId={avatarMap[d.authorUsername]} searchTerm={search.active ? keyword : ""} />)}
      </div>
      {archivedItems.length > 0 && (
        <div className={`mt-2 ${full ? "max-w-4xl w-full mx-auto" : ""}`}>
          <button data-testid="hub-disc-archived-toggle" onClick={() => setShowArchived(!showArchived)}
            className="w-full flex items-center justify-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-slate-600 rounded-lg border border-dashed border-slate-200 py-1.5 transition-colors">
            <Archive className="h-3 w-3" />{zh ? `已解决 · 归档讨论 ${archivedItems.length} 条` : `Archived ${archivedItems.length}`}
            <ChevronDown className={`h-3 w-3 transition-transform ${showArchived ? "rotate-180" : ""}`} />
          </button>
          {showArchived && (
            <div className="space-y-2.5 mt-2 opacity-75 max-h-[320px] overflow-y-auto" data-testid="hub-disc-archived-list">
              {archivedItems.map((d) => <DiscItem key={d.id} d={d} zh={zh} user={user} like={like} onReply={onReply} onPin={onPin} onArchive={onArchive} onTodo={setTodoDiscussion} onMessageAction={setMessageAction} avatarId={avatarMap[d.authorUsername]} searchTerm={search.active ? keyword : ""} />)}
            </div>
          )}
        </div>
      )}
      {search.active && search.hasMore && <button type="button" onClick={search.loadMore} disabled={search.loadingMore}
        data-testid="hub-disc-search-more" className={`mt-2 rounded-md border border-slate-200 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-brand-400 disabled:opacity-50 ${full ? "max-w-4xl w-full mx-auto" : "w-full"}`}>
        {search.loadingMore ? (zh ? "加载中…" : "Loading…") : (zh ? "加载更多匹配讨论" : "Load more matching discussions")}
      </button>}
      <div className={`mt-3 pt-3 border-t border-slate-100 ${full ? "shrink-0 max-w-4xl w-full mx-auto" : ""}`}>
        <div className="flex gap-1.5 mb-2 items-center flex-wrap">
          <button data-testid="hub-disc-vis-internal" onClick={() => setVis("internal")}
            className={`flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 ${vis === "internal" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500"}`}>
            <Lock className="h-3 w-3" />{zh ? "仅内部可见" : "Internal"}</button>
          <button data-testid="hub-disc-vis-owner" onClick={() => setVis("owner")}
            className={`flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 ${vis === "owner" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
            <span className={`h-1.5 w-1.5 rounded-full inline-block ${vis === "owner" ? "bg-white" : "bg-emerald-500"}`} />{zh ? "业主可见" : "Owner visible"}</button>
          <select value={related} onChange={(e) => setRelated(e.target.value)}
            className="ml-auto text-[10px] rounded border border-slate-200 px-1.5 py-1 bg-white max-w-[190px]" data-testid="hub-disc-related-select">
            <option value="">{zh ? "关联任务（可选）" : "Link task (optional)"}</option>
            {(tasks || []).filter((t) => !t.archived).map((t) => <option key={t.id} value={t.id}>{`${T(phases[t.phase])}·${T(t.title).slice(0, 18)}`}</option>)}
          </select>
        </div>
        <div className="relative">
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="hub-disc-attach-chips">
              {files.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-violet-50 text-violet-600 rounded-full px-2 py-0.5">
                  <Paperclip className="h-3 w-3" />{f.filename}
                  <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="hover:text-red-500" data-testid={`hub-disc-attach-remove-${i}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          {cands.length > 0 && (
            <div className="absolute bottom-full mb-2 left-0 z-30 bg-white rounded-xl shadow-xl border border-slate-100 p-1 w-56 max-h-44 overflow-y-auto" data-testid="hub-disc-mention-list">
              {cands.map((s) => (
                <button key={s.username} data-testid={`hub-disc-mention-pick-${s.username}`} onClick={() => pick(s)}
                  className="w-full text-left text-xs px-2 py-1.5 rounded-lg hover:bg-indigo-50 flex items-center justify-between">
                  <span className="font-semibold text-slate-700">{s.name}</span><span className="text-[9px] text-slate-400">{deptLabel(s.dept, zh)}</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 pr-16 items-start">
            <label className={`shrink-0 h-8 w-8 rounded-full border flex items-center justify-center cursor-pointer transition-colors ${upBusy ? "border-slate-200 text-slate-300" : "border-slate-200 text-slate-400 hover:text-violet-600 hover:border-violet-300"}`}
              title={zh ? "添加附件（图片 / PDF / 文档）" : "Attach files"} data-testid="hub-disc-attach-label">
              {upBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              <input type="file" multiple className="hidden" disabled={upBusy} accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={(e) => { if (e.target.files?.length) uploadFiles(Array.from(e.target.files)); e.target.value = ""; }} data-testid="hub-disc-attach-input" />
            </label>
            <textarea ref={inputRef} rows={1} value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                // 保留回车发送习惯，Shift+Enter 用于分段；输入法选字的 Enter 不能误发讨论。
                if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing || e.keyCode === 229) return;
                e.preventDefault();
                if (cands.length === 0) send();
              }}
              aria-label={zh ? "讨论内容" : "Discussion message"}
              placeholder={zh ? "发起讨论，@同事 可提醒（Enter 发送，Shift+Enter 换行）" : "Start a discussion, @ to mention (Enter to send, Shift+Enter for a new line)"}
              className="flex-1 min-w-0 max-h-40 resize-none text-xs leading-4 rounded-2xl border border-slate-200 px-3.5 py-2 focus:outline-none focus:border-violet-300 focus-visible:ring-2 focus-visible:ring-brand-400" data-testid="hub-disc-input" />
            <button onClick={send} disabled={busy || upBusy || (!text.trim() && files.length === 0)}
              className="shrink-0 whitespace-nowrap text-xs font-bold text-white bg-brand-500 hover:bg-brand-500 disabled:opacity-40 rounded-full px-4 py-2 flex items-center gap-1" data-testid="hub-disc-send">
              <Send className="h-3 w-3" />{zh ? "发送" : "Send"}</button>
          </div>
        </div>
      </div>
    </div>
  );
  // 放大时用 portal 挂到 body，避免被 sticky/transform 祖先限制层级（顶部导航栏遮挡）
  return <>
    {full ? createPortal(panel, document.body) : panel}
    {messageAction && <HubDiscussionMessageDialog key={`${messageAction.discussion.id}:${messageAction.reply?.id || ""}:${messageAction.action}`} target={messageAction} projectId={id} api={api} zh={zh}
      onClose={() => setMessageAction(null)} onChanged={({ item, visible }) => {
        setData((prev) => ({ ...prev, items: visible ? prev.items.map((d) => d.id === item.id ? item : d) : prev.items.filter((d) => d.id !== item.id) }));
        refreshSearch();
      }} />}
    {todoDiscussion && <HubDiscussionTodoDialog key={todoDiscussion.id} discussion={todoDiscussion} projectId={id} api={api} zh={zh} user={user}
      onClose={() => setTodoDiscussion(null)} onCreated={(todoId) => {
        setData((prev) => ({ ...prev, items: prev.items.map((d) => d.id === todoDiscussion.id ? { ...d, todoId } : d) }));
        load();
      }} />}
  </>;
};
